import { call, takeLatest } from 'redux-saga/effects';
import {
  fetchReaderTask,
  fetchParentReaderTask,
  saveReaderTask,
  saveParentReaderTask,
  completeReaderTask,
  completeParentReaderTask,
  startReaderTask,
  startParentReaderTask,
  SaveParentReaderTaskProps,
  CompleteParentReaderTaskProps,
} from 'src/api/readerTask';
import { actions } from './readerSubTask.slice';
import { Resp, dispatch } from '../helpers';
import {
  ReaderSubTaskCompleteAction,
  ReaderSubTaskFetchAction,
  ReaderSubTaskSaveAction,
  ReaderSubTaskStartAction,
} from './readerSubTask.types';
import { runToast } from '../tools';
import { AxiosError } from 'axios';

export function* fetchReaderSubTaskWorker(action: ReaderSubTaskFetchAction) {
  try {
    const { clinicalTrialId, subTaskId, taskId, endpoint } = action.payload;
    let response: Resp<typeof fetchReaderTask> | Resp<typeof fetchParentReaderTask>;
    if (taskId) {
      response = yield call(fetchParentReaderTask, {
        clinicalTrialId,
        endpoint,
        parentTaskId: taskId,
        taskId: subTaskId,
      });
    } else {
      response  = yield call(fetchReaderTask, {
        clinicalTrialId,
        endpoint,
        taskId: subTaskId,
      });
    }

    yield dispatch(actions.fetchReaderSubTaskSuccess(response.data));
    return response.data;
  } catch (error) {
    console.error('Error occur with reader data fetching', error);

    yield runToast('error', 'Error occur with reader data fetching');
    yield dispatch(actions.fetchReaderSubTaskFailure(error));
    return error;
  }
}

export function* startReaderSubTaskWorker(action: ReaderSubTaskStartAction) {
  try {
    const { clinicalTrialId, subTaskId, taskId } = action.payload;
    if (taskId) {
      yield call(startParentReaderTask, {
        clinicalTrialId,
        parentTaskId: taskId,
        taskId: subTaskId,
      });
    } else {
      yield call(startReaderTask, {
        clinicalTrialId,
        taskId: subTaskId,
      });
    }

    yield dispatch(actions.startReaderSubTaskSuccess('ok'));
    // Block on the refetch (call, not dispatch) so callers that read the
    // sub-task slice right after start see the post-start snapshot rather than
    // the stale PENDING one (D7-5719).
    yield call(fetchReaderSubTaskWorker, actions.fetchReaderSubTask(action.payload));
  } catch (error) {
    console.error('Error occur with reader data start', error);

    yield runToast('error', 'Error occur with reader data start');
    yield dispatch(actions.startReaderSubTaskFailure(error));
  }
}

export function* saveReaderSubTaskWorker(action: ReaderSubTaskSaveAction) {
  try {
    if (action.payload.taskId) {
      yield call(saveParentReaderTask, action.payload as SaveParentReaderTaskProps);
      } else {
        yield call(saveReaderTask, action.payload);
    }

    yield dispatch(
      actions.saveReaderSubTaskSuccess({
        status: 'ok',
        shouldRedirect: action.payload.shouldRedirect,
      })
    );
    yield runToast('success', 'Form data has been saved successfully');
  } catch (error) {
    console.error('Error occur with reader data saving', error);

    yield runToast('error', 'Error occur with reader data saving');
    yield dispatch(actions.saveReaderSubTaskFailure(error));
  }
}

export function* completeReaderSubTaskWorker(
  action: ReaderSubTaskCompleteAction
) {
  try {
    if (action.payload.taskId) {
      yield call(completeParentReaderTask, action.payload as CompleteParentReaderTaskProps);
    } else {
      yield call(completeReaderTask, action.payload);
    }


    yield dispatch(
      actions.completeReaderSubTaskSuccess({
        status: 'ok',
        shouldRedirect: action.payload.shouldRedirect,
      })
    );
    yield runToast('success', 'Form data has been signed successfully');
  } catch (error) {
    const err = error as AxiosError<{
      detail: string;
    }>;

    if (err.response?.status === 403) {
      yield runToast('error', err.response?.data?.detail);
    } else {
      yield runToast('error', 'Error occur with reader data signing');
    }

    console.error('Error occur with reader complete form', error);
    yield dispatch(actions.completeReaderSubTaskFailure(error));
  }
}

export function* readerSubTaskSaga() {
  yield takeLatest(actions.fetchReaderSubTask.type, fetchReaderSubTaskWorker);
  yield takeLatest(actions.startReaderSubTask.type, startReaderSubTaskWorker);
  yield takeLatest(actions.saveReaderSubTask.type, saveReaderSubTaskWorker);
  yield takeLatest(
    actions.completeReaderSubTask.type,
    completeReaderSubTaskWorker
  );
}
